import React, { forwardRef, HTMLAttributes, useCallback } from "react";
import { List, RowComponentProps } from "react-window";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ListSubheader, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/system";

const LISTBOX_PADDING = 8; // px

// Row component for v2
const RowComponent = (
  props: RowComponentProps<{ itemData: React.ReactElement<any>[] }>
) => {
  const { index, style, ariaAttributes, itemData } = props;
  const dataSet = itemData[index];

  const top =
    typeof style.top === "number"
      ? style.top
      : typeof style.top === "string"
        ? parseInt(style.top)
        : 0;

  const inlineStyle = { ...style, top };

  if (Object.prototype.hasOwnProperty.call(dataSet, "group")) {
    return (
      <ListSubheader
        key={(dataSet as any).key}
        component="div"
        style={inlineStyle}
        {...ariaAttributes}
      >
        {(dataSet as any).group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];

  return (
    <IgTypography
      key={key}
      component="li"
      {...optionProps}
      noWrap
      style={inlineStyle}
    >
      {dataSet[1]}
    </IgTypography>
  );
};

// Adapter for react-window
export const ListboxComponent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement<any>[] = [];
  (children as React.ReactElement<any>[]).forEach(
    (
      item: React.ReactElement<any> & { children?: React.ReactElement<any>[] }
    ) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactElement<any>) => {
    if (Object.prototype.hasOwnProperty.call(child, "group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  // Function-based row height for v2
  const rowHeight = useCallback(
    (index: number) => getChildSize(itemData[index]),
    [itemData, itemSize]
  );

  const listHeight = getHeight() + 2 * LISTBOX_PADDING;

  // Separate style from other props to ensure overflow is not overridden
  const { style: otherStyle, ...restProps } = other as any;

  return (
    <div
      ref={ref}
      {...restProps}
      style={{
        ...otherStyle,
        overflow: "visible",
      }}
    >
      <List
        rowComponent={RowComponent}
        rowProps={{ itemData }}
        rowHeight={rowHeight}
        rowCount={itemCount}
        defaultHeight={listHeight}
        style={{
          height: listHeight,
          width: "100%",
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  );
});
